import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import CreateJobModal from './CreateJobModal'
import ViewJobModal from './ViewJobModal'
import EditJobModal from './EditJobModal'

const JobManager = () => {
  const { t } = useTranslation()
  const [jobs, setJobs] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    paused: 0,
    pending: 0,
    failed: 0,
    completed: 0
  })
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [sortBy, setSortBy] = useState('updated_at')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)

  useEffect(() => {
    fetchJobs()
  }, [activeTab, sortBy])

  const fetchJobs = async () => {
    try {
      setLoading(true)
      const statusFilter = activeTab !== 'all' ? `?status_filter=${activeTab}` : ''
      // Use relative URL - nginx will proxy to backend
      const response = await fetch(`/api/jobs/${statusFilter}`)
      const data = await response.json()
      
      if (data.success) {
        setJobs(data.jobs)
        setStats(data.stats)
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error)
      toast.error(t('jobManager.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handlePauseJob = async (jobId) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/pause`, {
        method: 'POST'
      })
      const data = await response.json()
      
      if (data.success) {
        toast.success(t('jobManager.jobPaused'))
        fetchJobs()
      }
    } catch (error) {
      toast.error(t('jobManager.pauseFailed'))
    }
  }

  const handleResumeJob = async (jobId) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/resume`, {
        method: 'POST'
      })
      const data = await response.json()
      
      if (data.success) {
        toast.success(t('jobManager.jobResumed'))
        fetchJobs()
      }
    } catch (error) {
      toast.error(t('jobManager.resumeFailed'))
    }
  }

  const handleRunNow = async (jobId) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/run`, {
        method: 'POST'
      })
      const data = await response.json()
      
      if (data.success) {
        toast.success(t('jobManager.jobTriggered'))
        fetchJobs()
      }
    } catch (error) {
      toast.error(t('jobManager.runFailed'))
    }
  }

  const handleDeleteJob = async (jobId) => {
    if (!confirm(t('jobManager.deleteConfirm'))) return
    
    try {
      const response = await fetch(`/api/jobs/${jobId}`, {
        method: 'DELETE'
      })
      const data = await response.json()
      
      if (data.success) {
        toast.success(t('jobManager.jobDeleted'))
        fetchJobs()
      }
    } catch (error) {
      toast.error(t('jobManager.deleteFailed'))
    }
  }

  const handleViewJob = (job) => {
    setSelectedJob(job)
    setShowViewModal(true)
  }

  const handleEditJob = (job) => {
    setSelectedJob(job)
    setShowEditModal(true)
  }

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700',
      completed: 'bg-gray-100 text-gray-700'
    }
    
    const icons = {
      active: 'fa-play-circle',
      paused: 'fa-pause-circle',
      pending: 'fa-clock',
      failed: 'fa-exclamation-circle',
      completed: 'fa-check-circle'
    }
    
    return (
      <span className={`px-3 py-1 ${badges[status] || badges.pending} text-xs font-bold rounded-full flex items-center w-fit`}>
        <i className={`fa-solid ${icons[status]} mr-1 text-[8px]`}></i>
        {status.toUpperCase()}
      </span>
    )
  }

  const getFrequencyBadge = (frequency) => {
    const badges = {
      hourly: 'bg-orange-100 text-orange-700',
      daily: 'bg-blue-100 text-blue-700',
      weekly: 'bg-purple-100 text-purple-700',
      monthly: 'bg-indigo-100 text-indigo-700',
      custom: 'bg-teal-100 text-teal-700'
    }
    
    const icons = {
      hourly: 'fa-clock',
      daily: 'fa-calendar-day',
      weekly: 'fa-calendar-week',
      monthly: 'fa-calendar-alt',
      custom: 'fa-arrows-rotate'
    }
    
    return (
      <span className={`px-3 py-1 ${badges[frequency] || badges.custom} text-xs font-bold rounded-full`}>
        <i className={`fa-solid ${icons[frequency]} mr-1`}></i>
        {frequency.toUpperCase()}
      </span>
    )
  }

  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'N/A'
    const date = new Date(nextRun)
    const now = new Date()
    const diff = date - now
    
    if (diff < 0) return 'Overdue'
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (hours < 24) {
      return `In ${hours}h ${minutes}m`
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <section className="bg-white border-b border-light-200/20 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-3xl font-bold text-dark-300 mb-2 flex items-center">
                <i className="fa-solid fa-briefcase text-primary-500 mr-3"></i>
                {t('jobManager.title')}
              </h2>
              <p className="text-dark-100">{t('jobManager.subtitle')}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={fetchJobs}
              className="px-4 py-2 bg-light-100 hover:bg-light-200/20 rounded-lg text-sm font-medium text-dark-300 transition-colors flex items-center"
            >
              <i className="fa-solid fa-refresh mr-2"></i>
              {t('common.refresh')}
            </button>
            
            <button 
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 gradient-primary hover:opacity-90 text-white rounded-lg text-sm font-semibold transition-all shadow-md flex items-center"
            >
              <i className="fa-solid fa-plus mr-2"></i>
              {t('jobManager.createJob')}
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-6 border-t border-light-200/10 pt-4">
          {[
            { id: 'all', label: t('jobManager.allJobs') },
            { id: 'active', label: t('jobManager.active') },
            { id: 'paused', label: t('jobManager.paused') },
            { id: 'pending', label: t('jobManager.pending') },
            { id: 'failed', label: t('jobManager.failed') }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-primary-500 border-primary-500'
                  : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Statistics Cards */}
      <section className="grid grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-primary-500/10 to-primary-800/5 rounded-xl p-5 border border-primary-500/20">
          <div className="flex items-center justify-between mb-3">
            <i className="fa-solid fa-briefcase text-3xl text-primary-500"></i>
            <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-primary-500">{t('jobManager.stats.totalLabel')}</span>
          </div>
          <div className="text-3xl font-bold text-dark-300 mb-1">{stats.total}</div>
          <div className="text-sm text-dark-100">{t('jobManager.stats.total')}</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center justify-between mb-3">
            <i className="fa-solid fa-play-circle text-3xl text-green-600"></i>
            <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-green-600">{t('jobManager.stats.activeLabel')}</span>
          </div>
          <div className="text-3xl font-bold text-dark-300 mb-1">{stats.active}</div>
          <div className="text-sm text-dark-100">{t('jobManager.stats.running')}</div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 rounded-xl p-5 border border-yellow-200">
          <div className="flex items-center justify-between mb-3">
            <i className="fa-solid fa-pause-circle text-3xl text-yellow-600"></i>
            <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-yellow-600">{t('jobManager.stats.pausedLabel')}</span>
          </div>
          <div className="text-3xl font-bold text-dark-300 mb-1">{stats.paused}</div>
          <div className="text-sm text-dark-100">{t('jobManager.stats.pausedJobs')}</div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <i className="fa-solid fa-clock text-3xl text-blue-600"></i>
            <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-blue-600">{t('jobManager.stats.pendingLabel')}</span>
          </div>
          <div className="text-3xl font-bold text-dark-300 mb-1">{stats.pending}</div>
          <div className="text-sm text-dark-100">{t('jobManager.stats.scheduled')}</div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center justify-between mb-3">
            <i className="fa-solid fa-exclamation-circle text-3xl text-red-600"></i>
            <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-red-600">{t('jobManager.stats.failedLabel')}</span>
          </div>
          <div className="text-3xl font-bold text-dark-300 mb-1">{stats.failed}</div>
          <div className="text-sm text-dark-100">{t('jobManager.stats.needAttention')}</div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border border-light-200/20 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 gradient-primary text-white rounded-lg text-sm font-semibold flex items-center">
              <i className="fa-solid fa-check mr-2"></i>
              {t('jobManager.filters.allStatus')}
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-light-200/30 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="updated_at">{t('jobManager.filters.sortBy')} {t('jobManager.filters.lastModified')}</option>
              <option value="name">{t('jobManager.filters.sortBy')} {t('jobManager.filters.name')}</option>
              <option value="status">{t('jobManager.filters.sortBy')} {t('jobManager.filters.status')}</option>
              <option value="next_run">{t('jobManager.filters.sortBy')} {t('jobManager.filters.nextRun')}</option>
            </select>
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <i className="fa-solid fa-spinner fa-spin text-4xl text-primary-500"></i>
            <p className="mt-4 text-dark-100">{t('common.loading')}</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="gradient-primary rounded-xl shadow-lg p-8 text-center">
            <i className="fa-solid fa-briefcase text-6xl text-white mb-4"></i>
            <h3 className="text-2xl font-bold text-white mb-2">{t('jobManager.empty.noJobs')}</h3>
            <p className="text-white/90 mb-6">{t('jobManager.empty.noJobsDesc')}</p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="px-8 py-4 bg-white hover:bg-light-50 text-primary-800 rounded-lg text-lg font-bold shadow-lg transition-all transform hover:scale-105 flex items-center mx-auto"
            >
              <i className="fa-solid fa-plus text-2xl mr-3"></i>
              {t('jobManager.empty.createFirst')}
            </button>
          </div>
        ) : (
          jobs.map((job) => (
            <div key={job._id} className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6 hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-xl font-bold text-dark-300">{job.name}</h3>
                    {getStatusBadge(job.status)}
                    {getFrequencyBadge(job.schedule?.frequency || 'custom')}
                  </div>
                  
                  {job.description && (
                    <p className="text-sm text-dark-100 mb-4">{job.description}</p>
                  )}
                  
                  <div className="grid grid-cols-6 gap-4 mb-4">
                    <div className="bg-light-50 rounded-lg p-3">
                      <div className="text-xs text-dark-100 mb-1">{t('jobManager.table.template')}</div>
                      <div className="text-sm font-semibold text-dark-300">{job.template_id || t('common.custom')}</div>
                    </div>
                    
                    <div className="bg-light-50 rounded-lg p-3">
                      <div className="text-xs text-dark-100 mb-1">{t('jobManager.table.frequency')}</div>
                      <div className="text-sm font-semibold text-dark-300">{job.schedule?.frequency || t('jobManager.time.na')}</div>
                    </div>
                    
                    <div className="bg-light-50 rounded-lg p-3">
                      <div className="text-xs text-dark-100 mb-1">{t('jobManager.table.time')}</div>
                      <div className="text-sm font-semibold text-dark-300">{job.schedule?.time || t('jobManager.time.na')} UTC</div>
                    </div>
                    
                    <div className="bg-light-50 rounded-lg p-3">
                      <div className="text-xs text-dark-100 mb-1">{t('jobManager.table.nextRun')}</div>
                      <div className="text-sm font-semibold text-primary-500">{formatNextRun(job.next_run)}</div>
                    </div>
                    
                    <div className="bg-light-50 rounded-lg p-3">
                      <div className="text-xs text-dark-100 mb-1">{t('jobManager.table.successRate')}</div>
                      <div className="text-sm font-semibold text-green-600">{job.success_rate?.toFixed(1) || '0.0'}%</div>
                    </div>
                    
                    <div className="bg-light-50 rounded-lg p-3">
                      <div className="text-xs text-dark-100 mb-1">{t('jobManager.table.lastRun')}</div>
                      <div className="text-sm font-semibold text-dark-300">
                        {job.last_run ? new Date(job.last_run).toLocaleDateString() : t('jobManager.table.never')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center text-dark-100">
                      <i className="fa-solid fa-database text-primary-500 mr-2"></i>
                      <span>{t('jobManager.table.maxResults')}: <strong className="text-dark-300">{job.max_results}</strong></span>
                    </div>
                    <div className="flex items-center text-dark-100">
                      <i className="fa-solid fa-robot text-primary-800 mr-2"></i>
                      <span>{t('jobManager.table.model')}: <strong className="text-dark-300">{job.llm_model || t('jobManager.time.na')}</strong></span>
                    </div>
                    <div className="flex items-center text-dark-100">
                      <i className="fa-solid fa-play text-green-600 mr-2"></i>
                      <span>{t('jobManager.table.runs')}: <strong className="text-dark-300">{job.total_runs} {t('jobManager.table.times')}</strong></span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-6">
                  <button 
                    onClick={() => handleRunNow(job._id)}
                    className="p-3 hover:bg-light-50 rounded-lg transition-colors" 
                    title="Run Now"
                  >
                    <i className="fa-solid fa-play text-green-600 text-lg"></i>
                  </button>
                  
                  {job.status === 'paused' ? (
                    <button 
                      onClick={() => handleResumeJob(job._id)}
                      className="p-3 hover:bg-light-50 rounded-lg transition-colors" 
                      title="Resume"
                    >
                      <i className="fa-solid fa-circle-play text-green-600 text-lg"></i>
                    </button>
                  ) : (
                    <button 
                      onClick={() => handlePauseJob(job._id)}
                      className="p-3 hover:bg-light-50 rounded-lg transition-colors" 
                      title="Pause"
                    >
                      <i className="fa-solid fa-pause text-yellow-600 text-lg"></i>
                    </button>
                  )}
                  
                  <button 
                    onClick={() => handleEditJob(job)}
                    className="p-3 hover:bg-light-50 rounded-lg transition-colors" 
                    title="Edit"
                  >
                    <i className="fa-solid fa-pen text-primary-500 text-lg"></i>
                  </button>
                  
                  <button 
                    onClick={() => handleViewJob(job)}
                    className="p-3 hover:bg-light-50 rounded-lg transition-colors" 
                    title="View Details"
                  >
                    <i className="fa-solid fa-eye text-dark-100 text-lg"></i>
                  </button>
                  
                  <button 
                    onClick={() => handleDeleteJob(job._id)}
                    className="p-3 hover:bg-light-50 rounded-lg transition-colors" 
                    title="Delete"
                  >
                    <i className="fa-solid fa-trash text-red-600 text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </section>

      {/* Create Job Modal */}
      <CreateJobModal 
        isOpen={showCreateModal}
        onClose={(shouldRefresh) => {
          setShowCreateModal(false)
          if (shouldRefresh) {
            fetchJobs()
          }
        }}
      />

      {/* Edit Job Modal */}
      <EditJobModal 
        isOpen={showEditModal}
        job={selectedJob}
        onClose={(shouldRefresh) => {
          setShowEditModal(false)
          setSelectedJob(null)
          if (shouldRefresh) {
            fetchJobs()
          }
        }}
      />

      {/* View Job Modal */}
      <ViewJobModal 
        isOpen={showViewModal}
        job={selectedJob}
        onClose={() => {
          setShowViewModal(false)
          setSelectedJob(null)
        }}
      />
    </div>
  )
}

export default JobManager

